/**
 * Host switching — deciding whether an authenticated session belongs on a
 * different origin than the one it is currently on.
 *
 * Deployed builds pin the session to a known set of origins: `VITE_MAIN_URLS`
 * for ordinary users and `VITE_NEXT_URLS` for users on the `next` flag. When
 * the current origin is absent from the relevant allowlist, the app bounces the
 * browser to the first entry, preserving the path.
 *
 * The allowlists come from build-time env vars, so they are absent in any build
 * that does not set them — a local dev server or Playwright run started without
 * `src/web-app/.env` (it is gitignored, so it does not follow a checkout into a
 * new git worktree). An unset allowlist must mean "no host switching is
 * configured here", never "switch to the empty string": concatenating `''` with
 * the pathname produced a same-origin navigation to `/` on every boot, an
 * unbreakable redirect loop that left the app stuck on its root spinner.
 *
 * Hence the single invariant this module enforces: we only ever navigate away
 * to an absolute http(s) origin drawn from the allowlist. Anything else —
 * unset, empty, whitespace, or a relative value — is a misconfiguration, and
 * the safe response to a misconfigured allowlist is to stay put.
 */

/** Split a comma-separated allowlist into trimmed, non-empty entries. */
export function parseOriginAllowlist(allowlist: string | undefined | null): string[] {
  return (allowlist ?? '')
    .split(',')
    .map(url => url.trim())
    .filter(url => url.length > 0);
}

/** True when `origin` is listed in the comma-separated `allowlist`. */
export function isOriginInList(allowlist: string | undefined | null, origin: string): boolean {
  return parseOriginAllowlist(allowlist).includes(origin);
}

/**
 * An entry is only usable as a redirect target if it is an absolute http(s)
 * URL. This is what stops a blank or relative entry from being concatenated
 * with the current pathname into a same-origin navigation.
 */
function isAbsoluteHttpOrigin(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.href === `${parsed.origin}/`
    );
  } catch {
    return false;
  }
}

export interface HostSwitchTargetInput {
  /** Raw comma-separated allowlist, e.g. `import.meta.env.VITE_MAIN_URLS`. */
  allowlist: string | undefined | null;
  /** The origin the browser is on now, e.g. `window.location.origin`. */
  currentOrigin: string;
  /** Path to carry over — pathname + search + hash. */
  targetPath: string;
}

/**
 * Resolve the absolute URL the browser should be sent to, or `null` when it
 * should stay where it is.
 *
 * Returns `null` when:
 *   - the allowlist is unset, empty, or has no usable entries (not configured);
 *   - the current origin is already allowed (nothing to do);
 *   - no entry is an absolute http(s) origin (misconfigured — stay put).
 */
export function resolveHostSwitchTarget({
  allowlist,
  currentOrigin,
  targetPath,
}: HostSwitchTargetInput): string | null {
  const allowedOrigins = parseOriginAllowlist(allowlist);

  // Not configured for this build — host switching is off, not "go to ''".
  if (allowedOrigins.length === 0) return null;

  if (
    allowedOrigins.some(
      origin => isAbsoluteHttpOrigin(origin) && new URL(origin).origin === currentOrigin,
    )
  )
    return null;

  const destination = allowedOrigins.find(isAbsoluteHttpOrigin);
  if (!destination) return null;

  return destination.replace(/\/+$/, '') + targetPath;
}
